import * as R from 'ramda';

import { toConversionUnitInputs } from '@atom/components/common/conversionUnitsTable/conversionUnitUtilities';
import { PendingConversionUnits } from '@atom/components/schemaDetail/SchemaDetailContext';
import {
  ConversionUnit,
  ConversionUnitInput,
  SchemaConversionUnitBulkUpdateItem,
  SchemaTree,
} from '@atom/types/schema';

const UPDATABLE_FIELDS = [
  'unit',
  'conversionNumber',
  'active',
  'isDefault',
] as const;

/**
 * whether the conversion unit existed when the schema was last published, as
 * opposed to being added since. Published conversion units can't be edited or deleted.
 */
export const isPublishedConversionUnit = (
  id: string,
  schemaTree: Partial<SchemaTree>,
): boolean =>
  (schemaTree?.budget?.conversionUnits ?? []).some(
    conversionUnit => conversionUnit.id === id,
  );

/**
 * returns null when the working copy matches the saved list, so undoing every
 * change clears the pending state
 */
export const toPendingConversionUnits = (
  conversionUnits: ConversionUnit[],
  savedConversionUnits: ConversionUnit[],
): PendingConversionUnits =>
  R.equals(conversionUnits, savedConversionUnits) ? null : conversionUnits;

/**
 * the fields of a saved conversion unit that differ in its working copy.
 * isDefault: false is left out because the server clears the previous default
 * whenever another unit is sent as isDefault: true.
 */
const getChangedFields = (
  conversionUnit: ConversionUnit,
  savedConversionUnit: ConversionUnit,
): Omit<SchemaConversionUnitBulkUpdateItem, 'conversionUnitId'> =>
  R.pickBy(
    (value, field) =>
      value !== savedConversionUnit[field] &&
      !(field === 'isDefault' && !value),
    R.pick(UPDATABLE_FIELDS, conversionUnit),
  );

/**
 * splits the working copy into the payloads for the bulk create, update and
 * delete mutations. Rows not in the saved list are new, saved rows are only
 * sent when something changed, and saved rows missing from the working copy
 * are deleted. Saved rows can't be deleted on a published schema, so deletes
 * is always empty there.
 */
export const getConversionUnitBulkInputs = (
  conversionUnits: ConversionUnit[],
  savedConversionUnits: ConversionUnit[],
): {
  creates: ConversionUnitInput[];
  updates: SchemaConversionUnitBulkUpdateItem[];
  deletes: string[];
} => {
  const savedById = R.indexBy(R.prop('id'), savedConversionUnits);
  const [existing, created] = R.partition(
    conversionUnit => R.has(conversionUnit.id, savedById),
    conversionUnits,
  );
  const keptIds = new Set(existing.map(R.prop('id')));

  return {
    deletes: savedConversionUnits
      .map(R.prop('id'))
      .filter(id => !keptIds.has(id)),
    creates: toConversionUnitInputs(created),
    updates: existing.flatMap(conversionUnit => {
      const changes = getChangedFields(
        conversionUnit,
        savedById[conversionUnit.id],
      );

      return R.isEmpty(changes)
        ? []
        : [{ conversionUnitId: conversionUnit.id, ...changes }];
    }),
  };
};
